/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Search page V2 — gọi uniapp-backend (`/v2/dat/search`) thay cho n8n.
 *
 * Giao diện được giữ GIỐNG HỆT V1 (`src/pages/SearchPage.jsx`) để giáo
 * viên không bị lúng túng: layout raw Container/Row, đủ cột phiên học,
 * dải chỉ tiêu (metrics strip) inline. Chỉ khác: dữ liệu lấy từ V2 (BE
 * uniapp, camelCase) thay vì n8n (snake_case).
 *
 * Kết luận DAT do BE tính (GET /v2/dat/student/summary) — trang giáo
 * viên chỉ hiển thị (read-only). Metrics strip đọc từ `summary.totals`
 * + `summary.details` (BE tính sẵn theo hạng), thay cho reduce client
 * của V1. Khi kết quả gồm nhiều học viên (không single) → không có
 * summary, ẩn dải chỉ tiêu.
 *
 * Field names camelCase: sessionId / startTime / endTime / traineeName /
 * traineeRegistrationCode / vehicleHang / vehiclePlate / courseShortName /
 * autoDistance / autoDuration / nightDistance / nightDuration /
 * manualNightDuration / distanceSameHang / durationSameHang /
 * successRatio / instructorName.
 */
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import datV2 from '../../services/datV2';
import DatSummaryModal from '../../components/v2/DatSummaryModal';
import CopyableText from '../../components/CopyableText';
import { convertHoursToHM } from '../../utils';
import useMediaQuery from '../../hook/useMediaQuery';

export default function SearchPageV2() {
  const params = new URLSearchParams(window.location.search);
  const [drivingClass, setDrivingClass] = useState(params.get('class') || '');
  const [name, setName] = useState(params.get('name') || '');
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const loadSummary = async (regCode) => {
    if (!regCode) { setSummary(null); return; }
    try {
      const { data } = await datV2.getStudentSummary(regCode);
      setSummary(data || null);
    } catch (err) {
      console.error('summary failed', err);
      setSummary(null);
    }
  };

  const searchDatData = async () => {
    setLoading(true);
    setSummary(null);
    try {
      const { data } = await datV2.search({ name, drivingClass, page: 1, limit: 50 });
      const rows = Array.isArray(data) ? data : [];
      setRowData(rows);
      const first = rows[0]?.traineeRegistrationCode;
      const single = rows.length && rows.every((r) => r.traineeRegistrationCode === first) ? first : '';
      loadSummary(single);
    } catch (err) {
      console.error('search V2 failed', err);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (!name) {
      alert('Vui lòng nhập tên cần tra cứu');
      return;
    }
    searchDatData();
  };

  useEffect(() => {
    document.title = 'Tra cứu DAT';
    if (name) searchDatData();
  }, []);

  // Số liệu tổng: ưu tiên summary từ BE (đúng theo hạng). totals/details
  // shape khớp DatSummaryModal.
  const totals = summary?.totals || {};
  const details = summary?.details || {};
  const km = (v) => Math.floor((v || 0) * 100) / 100;
  const isPass = Boolean(summary?.valid && summary?.allChecked);
  // Giáo viên chỉ thấy "đã kiểm tra"/kết luận khi kết quả DAT ĐÃ được admin
  // phê duyệt (approved). Đã check nhưng chưa duyệt → vẫn hiện "Chưa kiểm tra".
  const approved = Boolean(summary?.approved);

  const nightDurationSameHang = (data) => {
    let val = 0;
    const ck = data.courseShortName;
    if (ck?.includes('B1') || ck?.includes('BTĐ')) {
      val = ['B1', 'B.01', 'B11'].includes(data.vehicleHang) ? (data.nightDuration || 0) : 0;
    } else if (ck?.includes('B2') || ck?.includes('BSS')) {
      val = ['B2', 'B'].includes(data.vehicleHang) ? (data.nightDuration || 0) : 0;
    } else if (ck?.includes('C1')) {
      val = ['C1', 'C'].includes(data.vehicleHang) ? (data.nightDuration || 0) : 0;
    }
    return val;
  };

  const colDefs = [
    {
      headerName: 'STT',
      valueGetter: (p) => (p.node ? p.node.rowIndex + 1 : ''),
      width: 80,
      pinned: 'left',
    },
    {
      field: 'checked',
      headerName: 'Trạng thái',
      flex: 1,
      minWidth: 120,
      cellRenderer: (p) =>
        (approved && p.data?.checked === true) ? (
          <span className='text-success'>Đã kiểm tra</span>
        ) : (
          <span className='text-danger'>Chưa kiểm tra</span>
        ),
    },
    { field: 'sessionId', headerName: 'Mã phiên' },
    { field: 'violation', headerName: 'Vi phạm', flex: 1, minWidth: 300 },
    {
      field: 'startTime',
      headerName: 'Thời gian bắt đầu',
      cellRenderer: (p) =>
        p.value ? new Date(p.value).toLocaleString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh' }) : '',
    },
    {
      field: 'endTime',
      headerName: 'Thời gian kết thúc',
      cellRenderer: (p) =>
        p.value ? new Date(p.value).toLocaleString('en-GB', { timeZone: 'Asia/Ho_Chi_Minh' }) : '',
    },
    {
      field: 'duration',
      headerName: 'Thời lượng (giờ)',
      cellRenderer: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    {
      field: 'distance',
      headerName: 'Quãng đường (km)',
      cellRenderer: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    { field: 'traineeName', headerName: 'Tên học viên', cellRenderer: (p) => <CopyableText value={p.value} /> },
    { field: 'courseShortName', headerName: 'Tên khoá học' },
    {
      field: 'autoDistance',
      headerName: 'Quãng đường TĐ',
      valueFormatter: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    {
      field: 'autoDuration',
      headerName: 'Thời gian TĐ',
      valueFormatter: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    {
      field: 'nightDistance',
      headerName: 'Quãng đường đêm',
      valueFormatter: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    {
      field: 'nightDuration',
      headerName: 'Thời gian đêm',
      valueFormatter: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    {
      field: 'nightDuration',
      headerName: 'Thời gian đêm đúng hạng',
      cellRenderer: (p) => nightDurationSameHang(p.data),
      valueFormatter: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    {
      field: 'manualNightDuration',
      headerName: 'TG đêm thủ công',
      valueFormatter: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    {
      field: 'distanceSameHang',
      headerName: 'Quãng đường đúng hạng',
      valueFormatter: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    {
      field: 'durationSameHang',
      headerName: 'Thời gian đúng hạng',
      valueFormatter: (p) => (p.value ? Math.floor(p.value * 100) / 100 : 0),
    },
    { field: 'successRatio', headerName: 'Tỷ lệ nhận diện' },
    { field: 'traineeRegistrationCode', headerName: 'Mã đăng ký học viên', cellRenderer: (p) => <CopyableText value={p.value} /> },
    { field: 'instructorName', headerName: 'Tên giáo viên' },
    { field: 'vehicleHang', headerName: 'Hạng xe' },
    { field: 'vehiclePlate', headerName: 'Biển số xe' },
    { field: 'source', headerName: 'Nguồn' },
  ];

  const StatusText = ({ status }) =>
    status === 'Đã đủ' ? (
      <span className='text-success'>{status}</span>
    ) : (
      <span className='text-danger'>{status}</span>
    );

  return (
    <Container style={{ height: '100vh' }} fluid>
      <Row className='d-flex justify-content-between align-items-center' style={{ height: '12%' }}>
        <Col xs={8} md={4}>
          <Form.Control
            type='text'
            placeholder='Nhập tên cần tra cứu'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch(e)}
          />
        </Col>
        <Col xs={4}>
          <Form.Control
            type='text'
            placeholder='Tên khoá học'
            value={drivingClass}
            onChange={(e) => setDrivingClass(e.target.value)}
          />
        </Col>
        <Col xs={12} md={4}>
          <Button onClick={handleSearch} variant='primary' disabled={loading} className='w-100'>
            Tra cứu
          </Button>
        </Col>
      </Row>
      <Row style={{ height: isDesktop ? '15%' : '35%' }}>
        {summary ? (
          <>
            <Col>
              Tổng TG:{' '}
              <span className='fw-bold'>
                {convertHoursToHM(totals.totalDuration)}
                <br />
                <StatusText status={details.totalTG} />
              </span>
            </Col>
            <Col>
              Tổng QĐ:{' '}
              <span className='fw-bold'>
                {km(totals.totalDistance)} km
                <br />
                <StatusText status={details.totalQD} />
              </span>
            </Col>
            <Col>
              TG đúng hạng:{' '}
              <span className='fw-bold'>
                {convertHoursToHM(totals.totalDurationDungHang)} <br />
                <StatusText status={details.TG_dung_hang} />
              </span>
            </Col>
            <Col>QĐ đúng hạng: {km(totals.totalDistanceDungHang)} km</Col>
            <Col>
              TG đêm đúng hạng:{' '}
              <span className='fw-bold'>
                {convertHoursToHM(totals.totalNightDurationDungHang)}
                <br />
                <StatusText status={details.TG_ban_dem_dung_hang} />
              </span>
              {totals.manualNightDuration ? (
                <div>(TG nhập tay: {convertHoursToHM(totals.manualNightDuration)})</div>
              ) : null}
            </Col>
            <Col>
              TG tự động:{' '}
              <span className='fw-bold'>
                {convertHoursToHM(totals.totalAutoDuration)}
                <br />
                <StatusText status={details.TG_tu_dong} />
              </span>
            </Col>
            <Col>
              QĐ tự động:{' '}
              <span className={km(totals.totalAutoDistance) > 120 ? 'text-danger fw-bold' : ''}>
                {km(totals.totalAutoDistance)} km
              </span>
            </Col>
            <Col>
              Kết luận:{' '}
              {!approved ? (
                <span className='text-danger fw-bold'>Chưa kiểm tra</span>
              ) : isPass ? (
                <span className='text-success fw-bold'>Đã đủ DAT</span>
              ) : (
                <span className='text-danger fw-bold'>
                  {summary.allChecked ? 'Chưa đủ điều kiện' : 'Chưa kiểm'}
                </span>
              )}{' '}
              <Button
                variant='link'
                size='sm'
                className='p-0 align-baseline'
                onClick={() => setShowSummaryModal(true)}
              >
                (chi tiết)
              </Button>
            </Col>
          </>
        ) : (
          <Col className='text-muted small d-flex align-items-center'>
            {rowData.length
              ? 'Kết quả gồm nhiều học viên — lọc theo 1 mã học viên để xem kết luận DAT.'
              : ''}
          </Col>
        )}
      </Row>
      {loading ? (
        <p className='text-center'>Đang tìm kiếm...</p>
      ) : (
        <Row className='ag-theme-quartz' style={{ height: isDesktop ? '73%' : '50%' }}>
          {rowData.length ? (
            <AgGridReact
              rowData={rowData}
              columnDefs={colDefs}
              gridOptions={{
                getRowStyle: (params) => {
                  if (params.data?.violation?.length) {
                    return { backgroundColor: '#FF6666' };
                  }
                  return null;
                },
              }}
            />
          ) : (
            <p className='text-center'>Không có dữ liệu</p>
          )}
        </Row>
      )}
      <DatSummaryModal
        show={showSummaryModal}
        onHide={() => setShowSummaryModal(false)}
        summary={summary}
        studentName={rowData[0]?.traineeName}
      />
    </Container>
  );
}
